<template>
	<div class="connection-details">
		<div class="header">
			<DetailsHeader
				:logoUrl="connection?.integration?.icon_url"
				:title="connection?.integration?.name"
			>
				<template #right-controls>
					<Button link @click="showDeleteModal = true">
						<TrashCanIcon type="light" left />
						<span v-t="'admin_integrations_delete_connection'" />
					</Button>
				</template>

				<template #status>
					<div class="connection-status">
						<CircleCheckIcon type="regular" />
						<span>{{ connection.status }}</span>
					</div>
				</template>
			</DetailsHeader>
		</div>

		<div v-if="connection.configuration_url" class="configuration">
			<div v-if="isLoading" class="loading-state">
				<Spinner />
				<div v-t="'admin_integrations_loading_state'"></div>
			</div>
			<iframe :src="connection.configuration_url" @load="isLoading = false" />
		</div>

		<DeleteModal
			v-if="showDeleteModal"
			@close="showDeleteModal = false"
			@delete="deleteConnection"
			:title="t('admin_integrations_delete_connection')"
			:showConfirm="true"
			:isProcessing="isDeleting"
			:bodyText="t('admin_integrations_delete_modal_body')"
			data-cy="deleteModal"
			:checkboxText="t('admin_integrations_delete_modal_confirm')"
		/>
	</div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Button, useAlert, Spinner } from '@/components/common'
import DeleteModal from '@/components/DeleteModal'
import { TrashCanIcon, CircleCheckIcon } from '@/icons'
import { get, del } from '../common'
import DetailsHeader from './DetailsHeader'

// Props
const props = defineProps({
	connectionId: {
		type: [String, Number],
		required: true,
	},
})

// Reactive State
const isLoading = ref(true)
const connection = ref({})
const showDeleteModal = ref(false)
const isDeleting = ref(false)

// Utilities
const router = useRouter()
const { t } = useI18n()
const alert = useAlert()

// Methods
async function fetchConnection() {
	try {
		const response = await get(
			`/organization/integrations/connections/${props.connectionId}`
		)
		connection.value = response.data
	} catch (e) {
		console.error(e)
		alert.error(t('common_api_error_save'))
	}
}

async function deleteConnection() {
	isDeleting.value = true
	try {
		await del(`/organization/integrations/connections/${props.connectionId}`)
		isDeleting.value = false
		router.push({ name: 'integrations', params: { type: 'CONNECTED' } })
	} catch (e) {
		console.error(e)
		isDeleting.value = false
		alert.error(t('common_api_error_save'))
	}
}

onMounted(async () => {
	await fetchConnection()
})
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';
.connection-details {
	.header {
		.pronto-btn,
		.pronto-icon {
			color: var(--red-400) !important;
		}

		.connection-status {
			display: flex;
			align-items: center;
			margin-left: 50px;
			.pronto-icon {
				color: var(--teal-300) !important;
				margin-right: 8px;
			}
			span {
				font-size: 14px;
				color: var(--neutral-600);
			}
		}
	}
	.configuration {
		height: calc(100vh - 125px);
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: column;
		iframe {
			height: 90%;
			width: 90%;
		}
		.loading-state {
			position: absolute;
			z-index: 1;
			top: 143px;
			bottom: 0;
			left: 65px;
			right: 0;
			background: var(--neutral-0);
			display: flex;
			align-items: center;
			justify-content: center;
			flex-direction: column;
		}
	}
}
</style>
